import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';

export interface ModuleDoc {
  id: string;
  key: string;
  name: string;
  description: string;
  status: 'active' | 'inactive';
  category: string;
  displayOrder: number;
  route: string | null;
  adminRoute: string | null;
  entitlementKey: string | null;
  viewPermission: string | null;
  adminPermission: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  scaffolded?: boolean;
}

interface ModulesContextType {
  modules: ModuleDoc[];
  approvedKeys: string[];
  labelFor: (key: string) => string;
  moduleByKey: (key: string) => ModuleDoc | undefined;
  ready: boolean;
  refresh: () => Promise<void>;
}

const ModulesContext = createContext<ModulesContextType>({
  modules: [],
  approvedKeys: [],
  labelFor: (k) => k,
  moduleByKey: () => undefined,
  ready: false,
  refresh: async () => {},
});

export const ModulesProvider = ({ children }: { children: ReactNode }) => {
  const [modules, setModules] = useState<ModuleDoc[]>([]);
  const [ready, setReady] = useState(false);

  const fetchModules = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/modules/active`, {
        credentials: 'include',
      });
      if (!res.ok) {
        // Unauthenticated (401) or other — treat as empty, caller can retry via refresh()
        setModules([]);
        return;
      }
      const data = await res.json();
      setModules(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[ModulesContext] fetch failed:', err);
      setModules([]);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const approvedKeys = modules.map((m) => m.key);
  const labelFor = (key: string): string => modules.find((m) => m.key === key)?.name || key;
  const moduleByKey = (key: string): ModuleDoc | undefined => modules.find((m) => m.key === key);

  return (
    <ModulesContext.Provider value={{ modules, approvedKeys, labelFor, moduleByKey, ready, refresh: fetchModules }}>
      {children}
    </ModulesContext.Provider>
  );
};

export const useModules = () => useContext(ModulesContext);
